import { useMutation } from "@tanstack/react-query";
import { CabecalhoPagina } from "../bento/ui";
import type { Json } from "@/types/database.generated";
import { CircleX, Copy, Pencil, Save } from "lucide-react";
import {
  Form,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { useFormState } from "react-hook-form";
import { RecordField } from "@/components/admin/record-field";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import ImageEditorField from "../misc/ImageEditorField";
import type { RAFile, Sale, SalesFormData } from "../types";
import { supabase } from "../providers/supabase/supabase";
import { caminhoDoAnexo } from "../providers/supabase/organizacao";
import { esquecerIdentidade } from "../providers/supabase/authProvider";
import {
  formatCnpj,
  formatCpf,
  isValidCnpj,
  isValidCpf,
  stripNonDigits,
} from "../contacts/document-validation";
import { CartaoConexoes } from "../conexoes/Conexoes";

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    if (fi.path) {
      const { error } = await supabase.storage
        .from("attachments")
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src).then((res) => res.blob())
    : fi.rawFile;

  const file = fi.rawFile;
  const fileExt = file.name.split(".").pop();
  const fileName = `${Math.random()}.${fileExt}`;
  const filePath = await caminhoDoAnexo(fileName);
  const { error: uploadError } = await supabase.storage
    .from("attachments")
    .upload(filePath, dataContent);

  if (uploadError) {
    throw new Error("Não foi possível enviar a imagem.");
  }

  const { data } = supabase.storage.from("attachments").getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;
  fi.type = file.type;

  return fi;
};

const normalizeAvatar = async (
  avatar?: RAFile | null,
): Promise<Omit<RAFile, "rawFile"> | null | undefined> => {
  if (!avatar) return avatar;

  const next = { ...avatar } as RAFile;
  if (next.rawFile || next.src.startsWith("blob:") || next.src.startsWith("data:")) {
    await uploadToBucket(next);
  }

  delete (next as Partial<RAFile>).rawFile;
  return next;
};

const updateUserProfile = async (
  userId: string,
  values: Partial<SalesFormData>,
) => {
  const { password: _password, ...rest } = values as Partial<SalesFormData> & {
    __clearAvatar?: boolean;
  };
  // UserFormData declara avatar como string, mas o ImageInput do react-admin
  // entrega um RAFile (é por isso que o código abaixo lê .src/.rawFile). O tipo
  // compartilhado fica como está; aqui a forma real é declarada localmente.
  const payload = { ...rest } as Omit<Partial<SalesFormData>, "avatar"> & {
    avatar?: Json;
    __clearAvatar?: boolean;
    cpf_cnpj_digits?: string;
    id?: number;
    user_id?: string;
    created_at?: string;
    updated_at?: string;
  };

  if ("avatar" in rest) {
    if (rest.avatar === null) {
      if (!rest.__clearAvatar) {
        delete payload.avatar;
      }
    } else if (rest.avatar) {
      payload.avatar = (await normalizeAvatar(rest.avatar as unknown as RAFile)) as Json;
    }
  }

  delete payload.__clearAvatar;
  // Colunas que vêm no record mas NÃO podem ser passadas no UPDATE:
  // - cpf_cnpj_digits é GENERATED ALWAYS AS (deriva do cpf_cnpj). Postgres
  //   recusa UPDATE explícito.
  // - id/user_id/created_at/updated_at são imutáveis pelo cliente.
  delete payload.cpf_cnpj_digits;
  delete payload.id;
  delete payload.user_id;
  delete payload.created_at;
  delete payload.updated_at;
  const { error: updateError } = await supabase
    .from("users")
    .update(payload)
    .eq("user_id", userId);

  if (updateError) {
    throw updateError;
  }

  const { data: updated, error: fetchError } = await supabase
    .from("users")
    .select("*")
    .eq("user_id", userId)
    .limit(1);

  if (fetchError) {
    throw fetchError;
  }

  if (updated?.length) {
    return updated[0];
  }

  const { data: authData, error: authError } = await supabase.auth.getUser();
  const authUser = authData?.user;
  if (authError || !authUser) {
    throw new Error("User not found");
  }

  const name =
    payload.name ||
    authUser.user_metadata?.name ||
    authUser.email?.split("@")[0] ||
    "User";
  const email = payload.email || authUser.email;

  const { data: created, error: createError } = await supabase
    .from("users")
    .insert({
      user_id: authUser.id,
      name,
      email,
      administrator: false,
      disabled: false,
      ...payload,
    } as never)
    .select("*")
    .limit(1);

  if (createError || !created?.length) {
    throw new Error(createError?.message || "Failed to create user");
  }

  return created[0];
};

export const SettingsPage = () => {
  const [isEditMode, setEditMode] = useState(false);
  const translate = useTranslate();
  const { identity, refetch: refetchIdentity } = useGetIdentity();
  const { data, refetch: refetchUser } = useGetOne("users", {
    id: identity?.id,
  });
  const notify = useNotify();
  const { mutate } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SalesFormData) => {
      if (!identity) {
        throw new Error("Record not found");
      }
      return updateUserProfile(String(identity.id), data);
    },
    onSuccess: () => {
      esquecerIdentidade();
      refetchIdentity();
      refetchUser();
      setEditMode(false);
      notify(
        translate("atomic.settings.profileUpdated", {
          _: "Perfil atualizado.",
        }),
      );
    },
    onError: (error: unknown) => {
      const message = (error as { message?: string } | null)?.message ?? "";
      // Friendly message for the unique-CPF/CNPJ constraint instead of the
      // raw "duplicate key value violates ..." Postgres error.
      if (/users_cpf_cnpj_digits_unique|cpf_cnpj.*duplicate|duplicate.*cpf_cnpj/i.test(message)) {
        notify("Já existe um usuário com este CPF/CNPJ.", { type: "error" });
        return;
      }
      notify(
        translate("atomic.settings.profileUpdateError", {
          _: "Não foi possível atualizar o perfil.",
        }),
        { type: "error" },
      );
    },
  });

  if (!identity) return null;

  const handleOnSubmit = async (values: any) => {
    mutate(values);
  };

  return (
    <div className="max-w-3xl mx-auto mt-2 space-y-5 px-1 sm:px-4">
      <CabecalhoPagina titulo="Minhas Informações" />
      <Form onSubmit={handleOnSubmit} record={data}>
        <SettingsForm isEditMode={isEditMode} setEditMode={setEditMode} />
      </Form>
    </div>
  );
};

const SettingsForm = ({
  isEditMode,
  setEditMode,
}: {
  isEditMode: boolean;
  setEditMode: (value: boolean) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const record = useRecordContext<Sale>();
  const { identity, refetch } = useGetIdentity();
  const { isDirty } = useFormState();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      notify(
        translate("atomic.settings.passwordMinLength", {
          _: "A senha deve ter pelo menos 6 caracteres",
        }),
        { type: "error" },
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      notify(
        translate("atomic.settings.passwordsNoMatch", {
          _: "As senhas não coincidem",
        }),
        { type: "error" },
      );
      return;
    }

    try {
      setIsChangingPassword(true);

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      notify("Senha alterada.");
      setPasswordDialogOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Error changing password:", error);
      notify(`Não foi possível alterar a senha: ${error.message}`, { type: "error" });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const { mutate: mutateSale } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SalesFormData) => {
      const userId = record?.user_id ?? identity?.id;
      if (!userId) {
        throw new Error("Record not found");
      }
      return updateUserProfile(String(userId), data);
    },
    onSuccess: () => {
      esquecerIdentidade();
      refetch();
      notify(
        translate("atomic.settings.profileUpdated", {
          _: "Perfil atualizado.",
        }),
      );
    },
    onError: () => {
      notify(
        translate("atomic.settings.profileUpdateError", {
          _: "Não foi possível atualizar o perfil.",
        }),
      );
    },
  });
  if (!identity) return null;

  const handleClickOpenPasswordChange = () => {
    setPasswordDialogOpen(true);
  };

  const handleAvatarUpdate = async (values: any) => {
    const payload = values?.src ? { avatar: values } : values;
    mutateSale(payload);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent>
          <div className="mb-4 flex flex-row justify-between">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("atomic.settings.myInfo")}
            </h2>
          </div>

          <div className="space-y-4 mb-4">
            <ImageEditorField
              source="avatar"
              type="avatar"
              onSave={handleAvatarUpdate}
              linkPosition="right"
            />
            <TextRender source="name" label="Nome" isEditMode={isEditMode} />
            <TextRender source="phone" label="Telefone" isEditMode={isEditMode} />
            <TextRender source="email" label="Email" isEditMode={isEditMode} />
            <CpfCnpjRender isEditMode={isEditMode} />
          </div>

          <div className="flex flex-row justify-end gap-2">
            {!isEditMode && (
              <>
                <Button
                  variant="outline"
                  type="button"
                  onClick={handleClickOpenPasswordChange}
                  className="bg-[#354660] text-white hover:bg-[#29374B] border-transparent dark:border-input dark:bg-transparent dark:text-foreground dark:hover:bg-accent"
                >
                  Alterar senha
                </Button>
              </>
            )}

            <Button
              type="button"
              variant={isEditMode ? "ghost" : "outline"}
              onClick={() => setEditMode(!isEditMode)}
              className={
                isEditMode
                  ? "flex items-center"
                  : "flex items-center bg-[#354660] text-white hover:bg-[#29374B] border-transparent dark:border-input dark:bg-transparent dark:text-foreground dark:hover:bg-accent"
              }
            >
              {isEditMode ? <CircleX /> : <Pencil />}
              {isEditMode
                ? translate("atomic.settings.cancel")
                : translate("atomic.settings.edit")}
            </Button>

            {isEditMode && (
              <Button
                type="submit"
                disabled={!isDirty}
                variant="outline"
                className="bg-[#354660] text-white hover:bg-[#29374B] border-transparent dark:border-input dark:bg-transparent dark:text-foreground dark:hover:bg-accent"
              >
                <Save />
                {translate("atomic.settings.save")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Servopa, Canopus e o que vier: a seção Conexões (28/10/2026). Perfil
          somente leitura não grava credencial (a RPC recusa). */}
      {!identity?.perfil_leitura && <CartaoConexoes superAdmin={!!identity?.is_super_admin} />}

      {/* Diálogo de troca de senha. Validação ao vivo (não espera o Salvar):
          regras visíveis no header, erros inline conforme o user digita,
          botão desabilitado até tudo ficar verde. */}
      {(() => {
        const lengthOk = newPassword.length >= 6;
        const matchOk = newPassword === confirmPassword;
        const lengthError = newPassword.length > 0 && !lengthOk
          ? "A senha precisa ter pelo menos 6 caracteres."
          : null;
        const matchError = confirmPassword.length > 0 && !matchOk
          ? "As senhas não coincidem."
          : null;
        const canSubmit = lengthOk && matchOk && !isChangingPassword;
        return (
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar senha</DialogTitle>
            <DialogDescription>
              Mínimo de 6 caracteres.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nova senha</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite a nova senha"
                disabled={isChangingPassword}
                aria-invalid={!!lengthError}
                className={lengthError ? "border-red-500 focus-visible:ring-red-500" : undefined}
              />
              {lengthError && (
                <p className="text-xs text-red-600 dark:text-red-400">{lengthError}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar senha</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme a nova senha"
                disabled={isChangingPassword}
                aria-invalid={!!matchError}
                className={matchError ? "border-red-500 focus-visible:ring-red-500" : undefined}
              />
              {matchError && (
                <p className="text-xs text-red-600 dark:text-red-400">{matchError}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPasswordDialogOpen(false);
                setNewPassword("");
                setConfirmPassword("");
              }}
              disabled={isChangingPassword}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={!canSubmit}
              className="bg-[#354660] text-white hover:bg-[#29374B] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
            >
              {isChangingPassword ? "Alterando..." : "Alterar senha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        );
      })()}

      {import.meta.env.VITE_INBOUND_EMAIL && (
        <Card>
          <CardContent>
            <div className="space-y-4 justify-between">
              <h2 className="text-xl font-semibold text-muted-foreground">
                {translate("atomic.settings.inboundEmail")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {translate("atomic.settings.inboundEmailDescription")}
              </p>
              <CopyPaste />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const TextRender = ({
  source,
  label,
  isEditMode,
}: {
  source: string;
  label?: string;
  isEditMode: boolean;
}) => {
  if (isEditMode) {
    return <TextInput source={source} label={label} helperText={false} />;
  }
  return (
    <div className="m-2">
      <RecordField source={source} label={label} />
    </div>
  );
};

const validateCpfCnpj = (value?: string) => {
  const digits = stripNonDigits(String(value ?? ""));
  if (!digits) return undefined;
  if (digits.length === 11) return isValidCpf(digits) ? undefined : "CPF inválido";
  if (digits.length === 14) return isValidCnpj(digits) ? undefined : "CNPJ inválido";
  return "CPF ou CNPJ inválido";
};

const formatCpfCnpj = (value?: string) => {
  const digits = stripNonDigits(String(value ?? ""));
  if (!digits) return "";
  return digits.length > 11 ? formatCnpj(digits) : formatCpf(digits);
};

const CpfCnpjRender = ({ isEditMode }: { isEditMode: boolean }) => {
  const record = useRecordContext<Sale & { cpf_cnpj?: string }>();
  if (isEditMode) {
    return (
      <TextInput
        source="cpf_cnpj"
        label="CPF ou CNPJ"
        helperText={false}
        format={formatCpfCnpj}
        parse={(value: string) => stripNonDigits(value)}
        validate={validateCpfCnpj}
      />
    );
  }
  return (
    <div className="m-2 space-y-1">
      <div className="text-xs text-muted-foreground">CPF ou CNPJ</div>
      <div className="text-sm">{formatCpfCnpj(record?.cpf_cnpj) || "-"}</div>
    </div>
  );
};

const CopyPaste = () => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    setCopied(true);
    navigator.clipboard.writeText(import.meta.env.VITE_INBOUND_EMAIL);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            onClick={handleCopy}
            variant="ghost"
            className="normal-case justify-between w-full"
          >
            <span className="overflow-hidden text-ellipsis">
              {import.meta.env.VITE_INBOUND_EMAIL}
            </span>
            <Copy className="h-4 w-4 ml-2" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{copied ? "Copiado" : "Copiar"}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

SettingsPage.path = "/settings";
